import { useState } from "react";
import { Drawer } from "@/components/ui/Drawer";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type Column } from "@/components/ui/DataTable";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Toolbar } from "@/components/ui/Toolbar";
import { Icon } from "@/components/ui/Icon";
import { useList } from "@/lib/api/queries";
import { dateTime, qty, relative, shortId, text } from "@/lib/format";
import { roleLabel } from "@/lib/labels";

interface AuditRecord {
  id: string;
  action_code: string;
  domain: string;
  source_service: string;
  source_kind: string;
  actor_user_id?: string;
  actor_role?: string;
  actor_username?: string;
  device_id?: string;
  ip_address?: string;
  entity_type: string;
  entity_id?: string;
  task_id?: string;
  order_id?: string;
  lp_id?: string;
  sku_id?: string;
  qty?: string;
  reason_code?: string;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  payload?: Record<string, unknown>;
  occurred_at: string;
}

export function Audit() {
  const [search, setSearch] = useState("");
  const [entityId, setEntityId] = useState("");
  const [selected, setSelected] = useState<AuditRecord | null>(null);

  const query = useList<AuditRecord>("audit", "/api/v1/audit-events", {
    entity_id: entityId.trim() || undefined,
    limit: 200,
  });

  const needle = search.trim().toLowerCase();
  const rows = (query.data?.items ?? []).filter((row) =>
    needle
      ? [row.action_code, row.actor_username, row.actor_role, row.entity_type, row.reason_code]
          .some((value) => value?.toLowerCase().includes(needle))
      : true,
  );

  const columns: Array<Column<AuditRecord>> = [
    {
      key: "when",
      header: "Qachon",
      render: (row) => <span title={dateTime(row.occurred_at)}>{relative(row.occurred_at)}</span>,
    },
    {
      key: "action",
      header: "Amal",
      render: (row) => <span className="mono">{row.action_code}</span>,
    },
    {
      key: "actor",
      header: "Kim",
      // Rol AMAL PAYTIDA nusxalangan: keyin xodim roli o'zgarsa
      // ham, bu yerda o'sha paytdagi rol turadi.
      render: (row) =>
        row.actor_username || row.actor_user_id ? (
          <div>
            <div>{text(row.actor_username) !== "—" ? row.actor_username : shortId(row.actor_user_id)}</div>
            {row.actor_role && <div className="cell-sub">{roleLabel(row.actor_role)}</div>}
          </div>
        ) : (
          <span style={{ color: "var(--outline)" }}>—</span>
        ),
    },
    { key: "entity", header: "Obyekt turi", render: (row) => <StatusBadge value={row.entity_type} tone="tag" /> },
    {
      key: "entity_id",
      header: "Obyekt",
      render: (row) =>
        row.entity_id ? (
          <button
            type="button"
            className="link-btn mono"
            onClick={() => setEntityId(row.entity_id!)}
            title="Shu obyektning butun izini ko'rish"
          >
            {shortId(row.entity_id)}
          </button>
        ) : (
          <span style={{ color: "var(--outline)" }}>—</span>
        ),
    },
    { key: "qty", header: "Miqdor", align: "right", render: (row) => <span className="num">{qty(row.qty)}</span> },
    { key: "source", header: "Manba", render: (row) => <StatusBadge value={row.source_kind} /> },
    {
      key: "ip",
      header: "IP",
      // IP faqat API manbali yozuvlarda bo'ladi: event konverti uni
      // tashimaydi.
      render: (row) => <span className="mono">{text(row.ip_address)}</span>,
    },
    {
      key: "open",
      header: "",
      align: "right",
      render: (row) => (
        <button type="button" className="btn btn-icon" onClick={() => setSelected(row)} title="Tafsilot">
          <Icon name="chevronRight" size={16} />
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Audit jurnali"
        hint="Append-only: yozuv o'zgartirilmaydi va o'chirilmaydi. Maxfiy qiymatlar saqlashdan oldin tozalangan."
      />

      <Toolbar
        search={search}
        onSearch={setSearch}
        placeholder="Amal, xodim yoki obyekt turi bo'yicha…"
        onRefresh={() => void query.refetch()}
        refreshing={query.isFetching}
      >
        <div className="search-box" style={{ minWidth: 280 }}>
          <Icon name="audit" size={16} className="icon-inside" />
          <input
            className="input"
            placeholder="Obyekt IDsi bo'yicha iz…"
            value={entityId}
            onChange={(event) => setEntityId(event.target.value)}
          />
        </div>
        {entityId && (
          <button type="button" className="btn btn-text btn-sm" onClick={() => setEntityId("")}>
            <Icon name="close" size={14} />
            Tozalash
          </button>
        )}
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={query.isLoading}
        error={query.error}
        onRetry={() => void query.refetch()}
        emptyTitle="Yozuv topilmadi"
        emptyHint="Audit jurnali barcha domen hodisalaridan quriladi."
        hasMore={query.data?.has_more}
        footer={<span>{rows.length} ta yozuv</span>}
      />

      {selected && <AuditDetail record={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function AuditDetail({ record, onClose }: { record: AuditRecord; onClose: () => void }) {
  return (
    <Drawer label="Audit yozuvi">
      <div className="drawer-head">
        <div>
          <div className="dialog-title mono">{record.action_code}</div>
          <div className="dialog-desc">{dateTime(record.occurred_at)}</div>
        </div>
        <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Yopish">
          <Icon name="close" />
        </button>
      </div>

      <div className="drawer-body">
        <dl className="kv">
          <dt>Manba servis</dt>
          <dd>{record.source_service}</dd>
          <dt>Manba turi</dt>
          <dd>{record.source_kind}</dd>
          <dt>Xodim</dt>
          <dd>
            {text(record.actor_username)}
            {record.actor_role ? ` · ${roleLabel(record.actor_role)}` : ""}
          </dd>
          <dt>Obyekt</dt>
          <dd className="mono">
            {record.entity_type} {record.entity_id ? `· ${record.entity_id}` : ""}
          </dd>
          {record.device_id && (
            <>
              <dt>Qurilma</dt>
              <dd className="mono">{record.device_id}</dd>
            </>
          )}
          {record.ip_address && (
            <>
              <dt>IP</dt>
              <dd className="mono">{record.ip_address}</dd>
            </>
          )}
          {record.reason_code && (
            <>
              <dt>Sabab</dt>
              <dd>{record.reason_code}</dd>
            </>
          )}
        </dl>

        {(record.before || record.after) && (
          <div className="section">
            <div className="section-title">Oldingi va keyingi holat</div>
            <div className="grid-2">
              <div>
                <div className="field-label">Oldin</div>
                <pre className="json-view">{JSON.stringify(record.before ?? {}, null, 2)}</pre>
              </div>
              <div>
                <div className="field-label">Keyin</div>
                <pre className="json-view">{JSON.stringify(record.after ?? {}, null, 2)}</pre>
              </div>
            </div>
          </div>
        )}

        <div className="section">
          <div className="section-title">To'liq payload</div>
          {/* Maxfiy kalitlar SERVERDA tozalangan: parol yoki token
              o'rnida `[redacted]` turadi. */}
          <pre className="json-view">{JSON.stringify(record.payload ?? {}, null, 2)}</pre>
        </div>
      </div>
    </Drawer>
  );
}
